import React, { memo, useMemo, Fragment } from 'react';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock } from '@modules/course/CourseBuilder/hooks';
import { Container } from 'reactstrap';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { useCourseBuilderContext } from '@modules/course/CourseBuilder/CourseBuilderPage';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { LibraryCustomBlockSettings } from './LibraryCustomBlockSettings';
import { JobDescriptionComponent } from './LibraryCustomBlockComponent';
import { TextBlock } from './LibraryCustomBlockComponent';
import { FormFieldsPreview } from '@modules/form/FormViewModal/FormViewModal';

function defineLibraryCustomBlockForm(variation: LibraryCustomBlockVariation) {
  return formGroup({
    heading: formControl<string>({ initialValue: '' }),
    text: formControl<string>({
      initialValue: `<ol><li>Vel magna iaculis arcu cursus parturient blandit suscipit taciti auctor maecenas.</li><li>Penatibus libero risus montes conubia pretium quisque ut fermentum, vulputate augue litora ornare fames turpis convallis imperdiet, curae etiam sed enim dictumst purus sit.</li><li>Tortor parturient gravida habitant dapibus et fusce felis nam nibh litora ridiculus aenean porttitor turpis pharetra.</li><li>Suscipit montes natoque aptent ex gravida orci ultrices volutpat nunc.</li></ol>`,
    }),
    fields: formControl<string>({
      initialValue: `[]`,
    }),
    id: formControl<string>({
      label: R.startCase(variation),
    }),
    advance: defineBlockAdvanceForm({ paddingTop: 0, paddingBottom: 0 }),
  });
}

export type LibraryCustomWidgetFormData = FormDataType<typeof defineLibraryCustomBlockForm>;
export type LibraryCustomWidgetForm = ReturnType<typeof defineLibraryCustomBlockForm>;

export enum LibraryCustomBlockVariation {
  libraryCustom = 'customLibraryItem',
}

export const LibraryCustomBlock = memo((props: CourseBuilder.BlockProps<LibraryCustomBlockVariation>) => {
  const { variation } = props;
  const form = useMemo(() => defineLibraryCustomBlockForm(variation), [variation]);
  const formValue = useNonNilObservable(form.value$);

  const content = useMemo(() => {
    if (CommonService.isJson(formValue.text)) {
      return JSON.parse(formValue.text) as any[];
    }
    return [];
  }, [formValue.text]);

  const fields = useMemo(() => {
    if (CommonService.isJson(formValue.fields)) {
      return JSON.parse(formValue.fields) as any[];
    }
    return [];
  }, [formValue.fields]);

  const hasContent = useMemo(() => fields.length > 0, [fields]);

  useConfigureBlock({
    props,
    form,
    contentComponent: LibraryCustomBlockSettings,
    contentProps: { form, variation },
  });

  return (
    <BlockContainer className={classNames('d-flex flex-fill', 'block-list', variation)} advanceForm={formValue.advance}>
      <Container>
        {!hasContent && (
          <React.Fragment>
            <h3>Library Custom Document</h3>
            <TextBlock title="Field 1">Overview</TextBlock>
            <TextBlock title="Field 2">Steps to Reproduce</TextBlock>
            <TextBlock title="Field 3">Images</TextBlock>
          </React.Fragment>
        )}
        {hasContent && <FormFieldsPreview fields={fields} values={content} />}
      </Container>
    </BlockContainer>
  );
});
